import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useSEO } from "../lib/seo";
import { slugify } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { JobsView } from "../components/JobsView";
import { EmptyState } from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Building2, MapPin, Globe, Briefcase, Linkedin, Users, Star,
  MessageSquare, Sparkles, Calendar, ChevronRight, BadgeCheck, Share2, Images,
} from "lucide-react";
import { jobCoverSrc, categoryCover } from "../lib/covers";

const BENEFIT_ICONS = {
  "Seguro de saúde": "🏥", "Refeição incluída": "🍽️", "Ginásio incluído": "🏋️",
  "Trabalho remoto": "🏠", "Horário flexível": "⏰", "22 dias de férias": "🏖️",
  "Formação contínua": "📚", "Bónus anual": "💰", "Viatura de empresa": "🚗",
  "Subsídio de transporte": "🚌", "Telemóvel de empresa": "📱", "Plano de pensões": "🪙",
  "Vale refeição": "🥗", "Seguro de vida": "🛡️", "Plano de ações": "📈",
};

function Stars({ rating, size = "md" }) {
  const sz = size === "sm" ? "w-3.5 h-3.5" : size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${sz} ${i <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          className="focus:outline-none transition-transform hover:scale-110">
          <Star className={`w-8 h-8 transition-colors ${i <= (hover || value) ? "fill-amber-400 text-amber-400" : "text-slate-200 hover:text-amber-200"}`} />
        </button>
      ))}
    </div>
  );
}

const RATING_LABEL = { 1: "Fraco", 2: "Razoável", 3: "Bom", 4: "Muito bom", 5: "Excelente" };

function StatChip({ label, value, icon: Icon, sub, link }) {
  const inner = (
    <div className="bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 h-full">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <p className="font-heading font-black tracking-tighter text-lg text-slate-900 mt-0.5 truncate">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
    </div>
  );
  if (link) return <Link to={link} className="block hover:opacity-80 transition-opacity">{inner}</Link>;
  return inner;
}

export default function CompanyPublic() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get(`/companies/public/${slug}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error("Empresa não encontrada:", err);
        setData(false);
      });
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const seo = useMemo(() => {
    if (!data?.company) return {};
    const c = data.company;
    return {
      title: `${c.company_name} — Vagas de emprego | FIRST2JOB`,
      description: `Vagas de emprego na ${c.company_name}${c.district ? ` (${c.district})` : ""}. ${data.jobs.length} vaga(s) ativa(s) no FIRST2JOB.${c.avg_rating ? ` Avaliação: ${c.avg_rating}/5.` : ""}`,
    };
  }, [data]);
  useSEO(seo);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Escolhe uma classificação"); return; }
    setSubmitting(true);
    try {
      await api.post(`/companies/${slug}/reviews`, { rating, comment: comment.trim() || undefined });
      toast.success("Avaliação submetida! Obrigado pelo teu feedback.");
      setReviewDialog(false);
      setRating(0);
      setComment("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  if (data === false) return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
        <h1 data-testid="company-not-found" className="font-heading font-black tracking-tighter text-2xl text-slate-900">Empresa não encontrada</h1>
        <p className="text-sm text-slate-500 mt-2">Esta empresa não existe ou ainda não foi aprovada.</p>
        <a href="/empregos" className="inline-block mt-8 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-6 py-2.5 transition-colors">Ver todas as vagas</a>
      </div>
      <Footer />
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /><p className="text-center py-32 text-slate-500">A carregar...</p></div>;

  const { company, jobs, reviews = [] } = data;
  const hasReview = user && reviews.some((r) => r.candidate_name === user.name);

  const BASE = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      {/* Social-network style profile header */}
      <div className="bg-white border-b border-black/5">
        {/* Cover photo */}
        <div className="relative h-48 sm:h-64 lg:h-72 bg-slate-100 overflow-hidden">
          {company.cover_url ? (
            <img src={`${BASE}${company.cover_url}`} alt="Capa da empresa"
              className="w-full h-full object-cover" loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
          )}
          {/* Subtle bottom gradient for legibility */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />
          {/* Breadcrumb over cover */}
          <div className="absolute top-4 left-0 right-0 z-10">
            <nav className="max-w-5xl mx-auto px-4 sm:px-8 flex items-center gap-1.5 text-white/80 text-xs">
              <Link to="/empregos" className="hover:text-amber-300 transition-colors">Empregos</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-white">{company.company_name}</span>
            </nav>
          </div>
        </div>

        {/* Overlapping avatar + name + actions */}
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 -mt-14 sm:-mt-16 pb-6 relative z-10">
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden bg-white flex items-center justify-center shrink-0 ring-4 ring-white shadow-lg">
              {company.logo_url ? (
                <img src={`${BASE}${company.logo_url}`} alt={`Logo ${company.company_name}`}
                  className="w-full h-full object-contain p-2"
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
              ) : null}
              <Building2 className={`w-14 h-14 text-amber-500 ${company.logo_url ? "hidden" : ""}`} strokeWidth={1.5} />
            </div>

            <div className="flex-1 min-w-0 sm:pb-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 data-testid="company-public-name"
                  className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-slate-900">
                  {company.company_name}
                </h1>
                {company.status === "approved" && (
                  <BadgeCheck className="w-6 h-6 text-sky-500" aria-label="Empresa verificada" />
                )}
              </div>
              {company.sector && (
                <p className="text-sm text-slate-500 mt-1">{company.sector}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 sm:pb-2 shrink-0">
              {user?.role === "candidate" && !hasReview && (
                <Button data-testid="review-btn" variant="outline"
                  className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-50"
                  onClick={() => setReviewDialog(true)}>
                  <Star className="w-3.5 h-3.5 mr-1.5" /> Avaliar
                </Button>
              )}
              <Button data-testid="share-btn" variant="outline" className="rounded-full"
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) navigator.share({ title: company.company_name, url }).catch(() => {});
                  else { navigator.clipboard.writeText(url); toast.success("Link copiado."); }
                }}>
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> Partilhar
              </Button>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pb-6">
            <StatChip label="Vagas ativas" value={jobs.length} icon={Briefcase} />
            <StatChip
              label="Avaliação"
              value={company.avg_rating ? `${company.avg_rating.toFixed(1)}/5` : "—"}
              icon={Star}
              sub={company.review_count ? `${company.review_count} review${company.review_count !== 1 ? "s" : ""}` : "Sem reviews"}
            />
            <StatChip label="Colaboradores" value={company.employee_count || "—"} icon={Users} />
            <StatChip label="Localização" value={company.district || "—"} icon={MapPin}
              link={company.district ? `/empregos-em/${slugify(company.district)}` : null} />
          </div>

          {/* Meta links row */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500 pb-6 border-b border-slate-100">
            {company.website && (
              <a href={company.website} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 hover:text-amber-600 transition-colors">
                <Globe className="w-4 h-4" /> {new URL(company.website).host.replace(/^www\./, "")}
              </a>
            )}
            {company.linkedin_url && (
              <a href={company.linkedin_url} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 hover:text-amber-600 transition-colors">
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
            )}
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> No FIRST2JOB desde {company.created_at ? new Date(company.created_at).toLocaleDateString("pt-PT", { month: "long", year: "numeric" }) : "—"}
            </span>
          </div>

          {/* Tab-like anchors */}
          <nav aria-label="Secções do perfil" className="flex gap-6 overflow-x-auto -mx-4 sm:-mx-0 px-4 sm:px-0 pt-1">
            {[
              { id: "about", label: "Sobre" },
              { id: "gallery", label: "Galeria" },
              { id: "jobs", label: `Vagas (${jobs.length})` },
              { id: "reviews", label: `Reviews (${reviews.length})` },
              { id: "benefits", label: "Benefícios" },
            ].map((t) => (
              <a key={t.id} href={`#${t.id}`} data-testid={`company-tab-${t.id}`}
                className="py-3 text-sm font-medium text-slate-600 border-b-2 border-transparent hover:border-amber-400 hover:text-slate-900 transition-colors whitespace-nowrap">
                {t.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 space-y-10">

        {/* About */}
        {company.about && (
          <section id="about" className="scroll-mt-20">
            <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-3">Sobre</h2>
            <div className="bg-white border border-black/5 rounded-xl p-6">
              <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">{company.about}</p>
            </div>
          </section>
        )}

        {/* Benefits */}
        {company.benefits?.length > 0 && (
          <section id="benefits" className="scroll-mt-20">
            <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-4">
              <Sparkles className="w-4 h-4 inline mr-2 text-amber-500" />Benefícios
            </h2>
            <div className="flex flex-wrap gap-2">
              {company.benefits.map((b) => (
                <Badge key={b} className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50 text-sm px-3 py-1">
                  {BENEFIT_ICONS[b] || "✓"} {b}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Culture */}
        {company.culture && (
          <section id="culture" className="scroll-mt-20">
            <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-3">
              Cultura & valores
            </h2>
            <div className="bg-white border border-black/5 rounded-xl p-6">
              <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{company.culture}</p>
            </div>
          </section>
        )}

        {/* Galeria — fotos das vagas ativas da empresa */}
        {jobs.length > 0 && (
          <section id="gallery" className="scroll-mt-20">
            <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Images className="w-4 h-4 text-amber-500" /> Galeria da empresa
              <span className="ml-2 text-xs font-medium text-slate-400">
                {jobs.length} foto{jobs.length !== 1 ? "s" : ""} das vagas ativas
              </span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {jobs.slice(0, 8).map((j) => (
                <Link key={j.id} to={`/empregos/${j.slug}`}
                  data-testid={`company-gallery-${j.slug}`}
                  className="group relative rounded-lg overflow-hidden bg-slate-100 aspect-[4/3] block">
                  <img
                    src={jobCoverSrc(j)}
                    alt={j.title}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = categoryCover(j.category, j.id); }}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-2 sm:p-3 text-white">
                    <p className="text-[10px] uppercase tracking-wider font-semibold truncate opacity-90">{j.category}</p>
                    <p className="text-xs sm:text-sm font-bold tracking-tight line-clamp-2">{j.title}</p>
                  </div>
                </Link>
              ))}
            </div>
            {jobs.length > 8 && (
              <p className="mt-3 text-xs text-slate-500 text-center">
                <a href="#jobs" className="hover:text-amber-600 underline underline-offset-2">Ver todas as {jobs.length} vagas ↓</a>
              </p>
            )}
          </section>
        )}

        {/* Jobs */}
        <section id="jobs" className="scroll-mt-20">
          <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-4">Vagas em aberto</h2>
          {jobs.length === 0 ? (
            <EmptyState icon={Briefcase} title="Sem vagas ativas"
              description="Esta empresa não tem vagas abertas neste momento." />
          ) : (
            <JobsView jobs={jobs} testIdPrefix="company-jobs" defaultLayout="grid" />
          )}
        </section>

        {/* Reviews */}
        <section id="reviews" className="scroll-mt-20">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900">
                Avaliações de colaboradores
              </h2>
              {company.avg_rating > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <Stars rating={company.avg_rating} size="sm" />
                  <span className="text-amber-600 font-bold text-sm">{company.avg_rating.toFixed(1)} / 5</span>
                  <span className="text-slate-400 text-xs">({company.review_count} avaliação{company.review_count !== 1 ? "s" : ""})</span>
                </div>
              )}
            </div>
            {user?.role === "candidate" && !hasReview && (
              <Button data-testid="review-btn" variant="outline"
                className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-50"
                onClick={() => setReviewDialog(true)}>
                <Star className="w-3.5 h-3.5 mr-1.5" /> Avaliar empresa
              </Button>
            )}
          </div>

          {reviews.length === 0 ? (
            <div className="bg-white border border-black/5 rounded-xl p-8 text-center">
              <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-slate-500">
                {user?.role === "candidate"
                  ? "Sê o primeiro a avaliar esta empresa. Partilha a tua experiência com outros candidatos."
                  : "Ainda sem avaliações. Candidatos podem avaliar empresas após se candidatarem."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`}
                  className="bg-white border border-black/5 rounded-xl p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Stars rating={r.rating} size="sm" />
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(r.created_at).toLocaleDateString("pt-PT", { year: "numeric", month: "long" })}
                      </p>
                    </div>
                    <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 shrink-0">
                      {RATING_LABEL[r.rating] || "Avaliação"}
                    </Badge>
                  </div>
                  {r.comment && (
                    <p className="text-sm text-slate-700 mt-3 leading-relaxed">{r.comment}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <Footer />

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tighter">
              Avaliar {company.company_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReview} className="space-y-5">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Classificação *</p>
              <StarPicker value={rating} onChange={setRating} />
              {rating > 0 && (
                <p className="text-sm text-amber-600 font-medium mt-1">{RATING_LABEL[rating]}</p>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Comentário (opcional)</p>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Partilha a tua experiência de candidatura com outros candidatos..."
                rows={4}
                maxLength={500}
                className="resize-none"
              />
              <p className="text-[11px] text-slate-400 mt-1 text-right">{comment.length}/500</p>
            </div>
            <p className="text-[11px] text-slate-400">
              A avaliação é anónima. Só podes avaliar empresas às quais te candidataste.
            </p>
            <DialogFooter>
              <Button type="submit" className="rounded-full bg-slate-900 hover:bg-slate-800 w-full"
                disabled={!rating || submitting}>
                {submitting ? "A enviar..." : "Submeter avaliação"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
